import Container from "@/components/ui/Container";
import SectionHeader from "@/components/ui/SectionHeader";
import Card from "@/components/ui/Card";
import BulletList from "@/components/ui/BulletList";
import { UserAvatar, AssistantAvatar } from "./icons/ChatAvatars";
import { DISCLAIMER } from "./constants";
import useInView from "@/hooks/useInView";

const featureCards = [
  {
    title: "Корректная интерпретация запросов",
    intro: "Система понимает запросы в свободной форме",
    bullets: ["исправляет опечатки", "учитывает разные форматы и написание"],
  },
  {
    title: "Устойчивость при больших объёмах данных",
    intro: "Анализ без потери качества даже при большом количестве данных",
    bullets: [
      "учитываются только значимые данные",
      "повторяющиеся сигналы не искажают результат",
    ],
  },
  {
    title: "Работа в разных сценариях",
    intro: "МСР-модуль может использоваться через:",
    bullets: ["веб-интерфейс", "командную строку", "автоматические сценарии"],
  },
  {
    title: "Внедрение за 15 минут",
    intro:
      "MCP подключается к Ключ-АСТРОМ и начинает работать с текущими данными",
    bullets: [
      "без перестройки инфраструктуры",
      "без потери накопленной информации",
    ],
  },
];

const chatMessages = [
  {
    isUser: true,
    sender: "Вы",
    time: "16:40",
    text: "Почему выросло время отклика по 99 перцентилю у payment-service за последние 2 часа?",
  },
  {
    isUser: false,
    sender: "Ассистент",
    time: "16:41",
    text: "Корневая причина найдена. В 14:32 деплой payment-service v2.4.1 внёс регрессию в SQL-запрос к orders_db. Количество slow queries выросло в 12х, p99 latency достигло 4.2 сек.",
  },
  {
    isUser: true,
    sender: "Вы",
    time: "16:42",
    text: "Угрожает ли это нашему SLO на этой неделе?",
  },
  {
    isUser: false,
    sender: "Ассистент",
    time: "16:44",
    text: "Риск высокий. При текущей динамике error budget будет исчерпан через ~6 ч. Рекомендую откат на v2.4.0.",
  },
];

const cardBody = "text-[16px] font-medium leading-[1.3] text-[#202020]";
const noteText = "text-[12px] font-medium leading-[1.3] text-[#999]";

const bubbleBase =
  "transition-[opacity,transform] duration-500 ease-out motion-reduce:!translate-y-0 motion-reduce:!opacity-100 motion-reduce:transition-none";
const revealDelays = ["", "delay-[150ms]", "delay-[300ms]", "delay-[450ms]"];

function FeatureCard({ title, intro, bullets }) {
  return (
    <Card variant="light" className="gap-5">
      <h3 className="text-[21px] font-bold leading-none tracking-[-0.21px] text-[#1c3564]">
        {title}
      </h3>
      <div className={cardBody}>
        <p>{intro}</p>
        <BulletList spacing="space-y-0" items={bullets} />
      </div>
    </Card>
  );
}

function ChatMessage({ isUser, sender, time, text, revealed, delayClass }) {
  const Avatar = isUser ? UserAvatar : AssistantAvatar;
  return (
    <div
      className={`flex flex-col gap-2.5 rounded-[5px] p-[18px] ${bubbleBase} ${delayClass} ${revealed} ${
        isUser ? "bg-[#001f40]" : "bg-[#202938]"
      }`}
    >
      <div className="flex items-center gap-3">
        <Avatar className="size-[44px] md:size-[26px]" />
        <span className="text-[14px] font-bold leading-none text-white">
          {sender}
        </span>
        <span className="text-[12px] font-medium leading-none text-[#999]">
          {time}
        </span>
      </div>
      <p className="text-[14px] font-medium leading-[1.3] text-white">{text}</p>
    </div>
  );
}

function ChatBox() {
  const [ref, inView] = useInView();
  const revealed = inView
    ? "translate-y-0 opacity-100"
    : "translate-y-4 opacity-0";

  return (
    <div
      ref={ref}
      className="flex flex-col gap-[18px] rounded-[25px] bg-[#1c3564] p-[26px] md:gap-2.5"
    >
      {chatMessages.map((msg, i) => (
        <ChatMessage
          key={i}
          {...msg}
          revealed={revealed}
          delayClass={revealDelays[i]}
        />
      ))}
    </div>
  );
}

export default function ChatDemoSection() {
  return (
    <section>
      <Container>
        <SectionHeader
          label="Видимость 360°"
          heading="Внедрение и использование модуля МСР в системе"
        />

        <div className="mt-10 flex flex-col gap-2.5 min-[478px]:mt-[60px] min-[478px]:gap-5 xl:mt-[84px] xl:flex-row">
          <div className="grid grid-cols-1 gap-2.5 min-[478px]:gap-5 sm:grid-cols-2 xl:w-1/2">
            {featureCards.map((card) => (
              <FeatureCard key={card.title} {...card} />
            ))}
          </div>

          {/* Chat dialog + disclaimer */}
          <div className="mx-auto flex w-full max-w-[324px] flex-col md:max-w-none xl:w-1/2">
            <ChatBox />
            <p className={`mt-5 ${noteText}`}>{DISCLAIMER}</p>
          </div>
        </div>
      </Container>
    </section>
  );
}
